import { ApiHttpError } from '@daily/contracts/api';
import type { AuthError, AuthErrorKind, UiError, UiErrorKind } from './types.js';

/** The backend's error code, when the failure came from the API with one. */
export function codeOf(error: unknown): string | null {
  if (!(error instanceof ApiHttpError)) return null;
  const payload = error.payload as { error?: { code?: unknown } } | undefined;
  return typeof payload?.error?.code === 'string' ? payload.error.code : null;
}

/** The current revision a conflict reports, when it does. */
export function currentRevisionOf(error: unknown): number | null {
  if (!(error instanceof ApiHttpError)) return null;
  const payload = error.payload as { error?: { details?: { currentRevision?: unknown } } } | undefined;
  const value = payload?.error?.details?.currentRevision;
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null;
}

const BY_CODE: Readonly<Record<string, UiErrorKind>> = {
  UNAUTHENTICATED: 'auth', PROFILE_REQUIRED: 'profile_required', QUOTA_EXCEEDED: 'too_large',
  GRID_LOCKED: 'conflict', DAY_NOT_OPEN: 'conflict', DAY_SLOT_USED: 'conflict', STATE_CONFLICT: 'conflict', REVISION_CONFLICT: 'conflict',
  MEDIA_NOT_READY: 'conflict', CONTENT_REQUIRED: 'validation',
};

/**
 * Whatever a backend call threw, in the UI's words (06 §3). The detail is for logs: the backend's
 * code or the HTTP status, never a message, post text or address.
 */
export function uiErrorOf(error: unknown): UiError {
  if (isUiError(error)) return error;
  if (error instanceof ApiHttpError) {
    const code = codeOf(error), detail = code ?? `http ${error.status}`;
    const byCode = code ? BY_CODE[code] : undefined;
    if (byCode) return { kind: byCode, detail };
    const status = error.status;
    const kind: UiErrorKind = status === 401 ? 'auth' : status === 403 ? 'forbidden' : status === 404 ? 'not_found' : status === 409 ? 'conflict'
      : status === 413 ? 'too_large' : status === 400 || status === 422 ? 'validation' : 'server';
    return { kind, detail };
  }
  if (error instanceof TypeError || (error instanceof Error && error.name === 'AbortError')) return { kind: 'network', detail: error.name };
  return { kind: 'server', detail: error instanceof Error ? error.name : 'unknown' };
}

export function isUiError(value: unknown): value is UiError {
  return !!value && typeof value === 'object' && !(value instanceof Error) && typeof (value as { kind?: unknown }).kind === 'string';
}

const AUTH_BY_CODE: Readonly<Record<string, AuthErrorKind>> = {
  INVALID_EMAIL_OR_PASSWORD: 'invalid', INVALID_PASSWORD: 'invalid', CREDENTIAL_ACCOUNT_NOT_FOUND: 'invalid', USER_NOT_FOUND: 'invalid',
  EMAIL_NOT_VERIFIED: 'unverified',
  HANDLE_TAKEN: 'handle_taken', HANDLE_INVALID: 'handle_invalid',
  USER_ALREADY_EXISTS: 'email_taken', USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'email_taken', INVALID_EMAIL: 'email_invalid',
  PASSWORD_TOO_SHORT: 'password_weak', PASSWORD_TOO_LONG: 'password_weak',
  INVALID_TOKEN: 'link_expired', TOKEN_EXPIRED: 'link_expired',
  TOO_MANY_REQUESTS: 'too_many',
};

/** A failed account step, in the sign-in screen's words. */
export function authErrorOf(code: string | null, status: number | null): AuthError {
  const byCode = code ? AUTH_BY_CODE[code] : undefined;
  if (byCode && code) return { kind: byCode, detail: code };
  if (status === 429) return { kind: 'too_many', detail: 'http 429' };
  if (status === null || status === 0) return { kind: 'network', detail: code ?? 'no response' };
  return { kind: 'server', detail: code ?? `http ${status}` };
}
